import React, { useState, useEffect } from "react";
import axios from "axios";
import { ChevronDown, RefreshCw, Dna, LineChart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => {
  if (!d) return d;
  if (d.length === 8) return `${d.slice(4, 6)}/${d.slice(6, 8)}`;
  if (d.length === 10 && d.includes("-")) return `${d.slice(5, 7)}/${d.slice(8, 10)}`;
  return d;
};

const Metric = ({ label, value, tone = "text-slate-100" }) => (
  <div className="flex flex-col items-center rounded-lg border border-slate-800 bg-slate-950/50 px-2.5 py-1.5">
    <span className="font-mono text-[8px] uppercase tracking-widest text-slate-500">{label}</span>
    <span className={`font-display text-base font-bold tabular-nums ${tone}`}>{value}</span>
  </div>
);

const GroupRow = ({ g, title }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-2.5" data-testid={`arch-perf-row-${(g.archetype || title).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="mb-1.5 flex items-center justify-between">
      <span className="font-display text-[12px] font-bold uppercase tracking-wide text-fuchsia-300">
        {g.archetype || title}{typeof g.count === "number" ? <span className="ml-1 text-slate-500">· {g.count}</span> : null}
      </span>
    </div>
    <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
      <Metric label="Appr Picks" value={g.approved_picks} />
      <Metric label="Appr Hits" value={g.approved_hits} tone="text-lime-300" />
      <Metric label="Appr HR" value={`${g.approved_hit_rate}%`} tone="text-lime-300" />
      <Metric label="Watch Picks" value={g.watch_picks} />
      <Metric label="Watch Hits" value={g.watch_hits} tone="text-amber-300" />
      <Metric label="Watch HR" value={`${g.watch_hit_rate}%`} tone="text-amber-300" />
    </div>
    <div className="mt-1.5 grid grid-cols-2 gap-1.5 sm:grid-cols-2">
      <Metric label="Captured" value={`${g.captured} / ${g.total_scorers}`} tone="text-cyan-300" />
      <Metric label="Capture Rate" value={`${g.capture_rate}%`} tone="text-cyan-300" />
    </div>
  </div>
);

const PlayerResult = ({ p }) => (
  <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-900/40 px-2.5 py-1.5" data-testid={`arch-player-${p.name}`}>
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <span className="truncate font-display text-[12px] font-bold uppercase tracking-tight text-slate-100">{p.name}</span>
        <span className={`shrink-0 rounded px-1 py-0.5 font-mono text-[8px] font-bold uppercase ${p.status === "APPROVED" ? "bg-lime-950/50 text-lime-300" : "bg-amber-950/50 text-amber-300"}`}>{p.status}</span>
      </div>
      <div className="mt-0.5 truncate font-mono text-[9px] uppercase tracking-wider text-slate-500">
        {p.team} • {p.position} • {p.game}
      </div>
      <div className="mt-0.5 flex flex-wrap gap-1 font-mono text-[9px]">
        <span className="rounded bg-fuchsia-950/60 px-1 text-fuchsia-300">{p.primary}</span>
        {p.secondary && <span className="rounded bg-fuchsia-950/40 px-1 text-fuchsia-400/80">{p.secondary}</span>}
      </div>
    </div>
    <span className={`shrink-0 rounded px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase ${p.scored ? "bg-lime-500 text-slate-950" : "bg-slate-800 text-slate-500"}`} data-testid={`arch-result-${p.name}`}>
      {p.scored ? `GOAL ✓${p.goals > 1 ? ` ×${p.goals}` : ""}` : "NO GOAL"}
    </span>
  </div>
);

const TrendBars = ({ audits, field, color, label }) => {
  const max = Math.max(100, ...audits.map((a) => a[field] || 0));
  return (
    <div data-testid={`trend-${field}`}>
      <div className="mb-1 flex items-center justify-between font-mono text-[9px] uppercase tracking-widest">
        <span className="text-slate-400">{label}</span>
        <span className={color.text}>latest {audits[audits.length - 1]?.[field] ?? 0}%</span>
      </div>
      <div className="flex h-16 items-end gap-1">
        {audits.map((a) => (
          <div key={a.date} className="group relative flex flex-1 flex-col items-center justify-end" title={`${fmtDate(a.date)} · ${a[field]}%`}>
            <div className={`w-full rounded-t ${color.bar}`} style={{ height: `${Math.max(3, ((a[field] || 0) / max) * 100)}%` }} />
            <span className="mt-0.5 font-mono text-[7px] text-slate-600">{fmtDate(a.date)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AuditHistory = () => {
  const [audits, setAudits] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/archetype-audit-history`, { timeout: 30000 })
      .then((r) => { if (alive) setAudits(r.data.audits || []); })
      .catch(() => { if (alive) setAudits([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return <p className="py-6 text-center font-mono text-[10px] uppercase tracking-widest text-slate-600" data-testid="audit-history-loading">Loading history…</p>;
  if (!audits.length) return <p className="py-6 text-center font-mono text-[10px] uppercase tracking-widest text-slate-600" data-testid="audit-history-empty">No slate audits stored yet — open Archetypes on completed slates to build the trend</p>;

  return (
    <div className="space-y-3" data-testid="audit-history">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <TrendBars audits={audits} field="approved_hit_rate" label="Approved Hit Rate" color={{ bar: "bg-lime-500/70", text: "text-lime-300" }} />
        <TrendBars audits={audits} field="watch_hit_rate" label="Watch Hit Rate" color={{ bar: "bg-amber-500/70", text: "text-amber-300" }} />
        <TrendBars audits={audits} field="capture_rate" label="Capture Rate" color={{ bar: "bg-cyan-500/70", text: "text-cyan-300" }} />
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-full min-w-[560px] border-collapse">
          <thead>
            <tr className="bg-slate-900/60 font-mono text-[8px] uppercase tracking-wider text-slate-500">
              <th className="px-2 py-1.5 text-left">Date</th>
              <th className="px-2 py-1.5 text-right">Games</th>
              <th className="px-2 py-1.5 text-right">Scorers</th>
              <th className="px-2 py-1.5 text-right">Appr P/H</th>
              <th className="px-2 py-1.5 text-right">Appr HR</th>
              <th className="px-2 py-1.5 text-right">Watch P/H</th>
              <th className="px-2 py-1.5 text-right">Watch HR</th>
              <th className="px-2 py-1.5 text-right">Captured</th>
              <th className="px-2 py-1.5 text-right">Capture</th>
            </tr>
          </thead>
          <tbody>
            {[...audits].reverse().map((a) => (
              <tr key={a.date} className="border-t border-slate-800/60 font-mono text-[10px]" data-testid={`audit-row-${a.date}`}>
                <td className="px-2 py-1.5 font-bold text-slate-200">{fmtDate(a.date)}</td>
                <td className="px-2 py-1.5 text-right text-slate-400">{a.total_games}</td>
                <td className="px-2 py-1.5 text-right text-slate-400">{a.total_scorers}</td>
                <td className="px-2 py-1.5 text-right text-slate-300">{a.approved_picks}/{a.approved_hits}</td>
                <td className="px-2 py-1.5 text-right font-bold text-lime-300">{a.approved_hit_rate}%</td>
                <td className="px-2 py-1.5 text-right text-slate-300">{a.watch_picks}/{a.watch_hits}</td>
                <td className="px-2 py-1.5 text-right font-bold text-amber-300">{a.watch_hit_rate}%</td>
                <td className="px-2 py-1.5 text-right text-slate-300">{a.captured}/{a.total_scorers}</td>
                <td className="px-2 py-1.5 text-right font-bold text-cyan-300">{a.capture_rate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function ArchetypePerformance({ perf, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  if (!perf?.overall) return null;
  const o = perf.overall;
  return (
    <div className="rounded-xl border border-fuchsia-900/40 bg-gradient-to-r from-fuchsia-950/15 to-slate-900/20 p-4" data-testid="archetype-performance">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-display text-[15px] font-bold uppercase tracking-wide text-fuchsia-300">
          <Dna className="h-4 w-4" /> Archetype Performance
        </h3>
        <button
          data-testid="arch-perf-refresh"
          onClick={onRefresh}
          className="flex items-center gap-1.5 rounded-lg border border-cyan-500/30 bg-cyan-950/30 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-cyan-300 hover:bg-cyan-950/60"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Live Audit
        </button>
      </div>

      <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
        <Metric label="Appr Picks" value={o.approved_picks} />
        <Metric label="Appr Hits" value={o.approved_hits} tone="text-lime-300" />
        <Metric label="Appr Hit Rate" value={`${o.approved_hit_rate}%`} tone="text-lime-300" />
        <Metric label="Watch Picks" value={o.watch_picks} />
        <Metric label="Watch Hits" value={o.watch_hits} tone="text-amber-300" />
        <Metric label="Watch Hit Rate" value={`${o.watch_hit_rate}%`} tone="text-amber-300" />
      </div>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
        <Metric label="Captured" value={`${o.captured} / ${o.total_scorers}`} tone="text-cyan-300" />
        <Metric label="Capture Rate" value={`${o.capture_rate}%`} tone="text-cyan-300" />
      </div>
      <p className="mt-2 text-center font-mono text-[9px] uppercase tracking-widest text-slate-500">
        Hit = player scored a GOAL · Approved / Watch / Capture tracked separately
      </p>

      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="arch-perf-expand"
        className="mt-3 flex w-full items-center justify-between rounded-lg border border-slate-800 bg-slate-900/50 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-300 hover:bg-slate-900/80"
      >
        <span>By Archetype ({perf.archetypes?.length || 0}) + Player Results ({perf.players?.length || 0})</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <div className="space-y-2">
            {(perf.archetypes || []).map((g) => <GroupRow key={g.archetype} g={g} />)}
          </div>
          <div>
            <p className="mb-1.5 font-mono text-[9px] uppercase tracking-widest text-slate-500">Player Results</p>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {(perf.players || []).map((p) => <PlayerResult key={`${p.team}-${p.jersey}-${p.name}`} p={p} />)}
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setHistOpen((v) => !v)}
        data-testid="arch-history-expand"
        className="mt-2 flex w-full items-center justify-between rounded-lg border border-slate-800 bg-slate-900/50 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-300 hover:bg-slate-900/80"
      >
        <span className="flex items-center gap-1.5"><LineChart className="h-3.5 w-3.5 text-cyan-400" /> Audit History — hit &amp; capture rate over time</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${histOpen ? "rotate-180" : ""}`} />
      </button>
      {histOpen && <div className="mt-3"><AuditHistory /></div>}
    </div>
  );
}
